import React, { useCallback, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  exportAdminDashboard,
  getAdminDashboard,
} from "../../api/adminDashboardApi";
import ModuleHeader, { ModuleHeaderAction } from "../../components/ModuleHeader/ModuleHeader";

import "./dashboard.scss";

const Icon = ({ name }) => {
  const paths = {
    bag: <><path d="M6 8h12l-1 12H7L6 8Z" /><path d="M9 8V6a3 3 0 0 1 6 0v2" /></>,
    orders: <><rect x="6" y="4" width="12" height="16" rx="2" /><path d="M9 4.5h6V7H9zM9 11h6M9 15h6" /></>,
    sold: <><path d="M5 9h14l-1.5 10h-11L5 9Z" /><path d="m9 9 3-5 3 5M9 13h.01M15 13h.01" /></>,
    pickup: <><circle cx="12" cy="12" r="8" /><path d="M12 8v5l3 2" /></>,
    collected: <><circle cx="12" cy="12" r="8" /><path d="m8.5 12 2.2 2.2 4.8-5" /></>,
    cancelled: <><circle cx="12" cy="12" r="8" /><path d="m9 9 6 6m0-6-6 6" /></>,
    merchant: <><path d="M5 10h14l-1-5H6l-1 5Z" /><path d="M6 10v9h12v-9M9 19v-5h6v5" /></>,
    selling: <><circle cx="12" cy="8" r="3" /><path d="M6 20c.5-4 2.5-6 6-6s5.5 2 6 6" /></>,
    sales: <><rect x="4" y="6" width="16" height="12" rx="2" /><circle cx="12" cy="12" r="2.5" /><path d="M7 9h.01M17 15h.01" /></>,
    revenue: <><path d="M8 18 16 6" /><circle cx="7.5" cy="7.5" r="2" /><circle cx="16.5" cy="16.5" r="2" /></>,
    average: <><path d="M5 18V9M12 18V5M19 18v-7" /><path d="M3 20h18" /></>,
    alert: <><path d="M12 4 3.5 19h17L12 4Z" /><path d="M12 9v4M12 16h.01" /></>,
    arrow: <path d="m9 6 6 6-6 6" />,
    refresh: <><path d="M20 11a8 8 0 1 0 1 4" /><path d="M20 4v7h-7" /></>,
    calendar: <><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16" /></>,
  };

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {paths[name]}
    </svg>
  );
};

const MetricCard = ({ item }) => (
  <article className="dashboard-page__metric-card">
    <span className="dashboard-page__metric-icon">
      <Icon name={item.icon} />
    </span>
    <div>
      <span>{item.label}</span>
      <strong>{item.value}</strong>
    </div>
  </article>
);

const SectionHeader = ({ title, description, action }) => (
  <div className="dashboard-page__section-header">
    <div>
      <h3>{title}</h3>
      {description ? <p>{description}</p> : null}
    </div>
    {action}
  </div>
);

const formatCurrency = (value) => `RM${Number(value || 0).toLocaleString("en-MY", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const formatDateTime = (value) => value
  ? new Intl.DateTimeFormat("en-MY", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(value))
  : "—";

const formatTrendDate = (value) => new Intl.DateTimeFormat("en-MY", {
  day: "numeric",
  month: "short",
}).format(new Date(`${value}T00:00:00`));

const formatStatus = (value) => ({
  completed: "Collected",
  collected: "Collected",
  ready: "Ready for Pickup",
  ready_for_pickup: "Ready for Pickup",
  cancelled: "Cancelled",
  refunded: "Cancelled",
  paid: "Paid",
  pending: "Pending",
}[value] || value);

const DONUT_SEGMENT_COLORS = {
  paid: "var(--color-info)",
  ready: "var(--color-primary)",
  collected: "var(--color-brand-600)",
  cancelled: "var(--color-danger)",
};

const buildDonutBackground = (segments) => {
  const total = segments.reduce((sum, item) => sum + Number(item.value || 0), 0);
  if (total <= 0) {
    return "var(--color-border-hairline)";
  }

  let current = 0;
  const stops = segments
    .filter((item) => Number(item.value) > 0)
    .map((item) => {
      const start = current;
      current += (Number(item.value) / total) * 100;
      return `${DONUT_SEGMENT_COLORS[item.className]} ${start}% ${current}%`;
    });

  return stops.length ? `conic-gradient(${stops.join(", ")})` : "var(--color-border-hairline)";
};

const DashboardPage = () => {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const loadDashboard = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      setDashboard(await getAdminDashboard());
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleExport = async () => {
    setIsExporting(true);
    setErrorMessage("");

    try {
      await exportAdminDashboard();
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setIsExporting(false);
    }
  };

  const marketplace = dashboard?.marketplace || {};
  const merchantOverview = dashboard?.merchant_overview || {};
  const financial = dashboard?.financial || {};
  const marketplaceStats = [
    { icon: "bag", label: "Bags Listed", value: marketplace.bags_listed || 0 },
    { icon: "sold", label: "Bags Sold", value: marketplace.bags_sold || 0 },
    { icon: "orders", label: "Orders", value: marketplace.orders || 0 },
    { icon: "pickup", label: "Ready for Pickup", value: marketplace.ready_for_pickup || 0 },
    { icon: "collected", label: "Collected", value: marketplace.collected || 0 },
    { icon: "cancelled", label: "Cancelled", value: marketplace.cancelled || 0 },
  ];
  const merchantStats = [
    { icon: "merchant", label: "Active Outlets", value: merchantOverview.active_outlets || 0 },
    { icon: "selling", label: "Selling Today", value: merchantOverview.selling_today || 0 },
  ];
  const financialStats = [
    { icon: "sales", label: "Sales", value: formatCurrency(financial.sales) },
    { icon: "revenue", label: "Commission", value: formatCurrency(financial.commission) },
  ];
  const pendingRefunds = (dashboard?.action_required || []).map((order) => ({
    order: order.order_number,
    merchant: order.merchant,
    customer: order.customer,
    date: formatDateTime(order.created_at),
    amount: formatCurrency(order.amount),
  }));
  const recentOrders = (dashboard?.recent_orders || []).map((order) => ({
    order: order.order_number,
    merchant: order.merchant,
    outlet: order.outlet,
    customer: order.customer,
    status: formatStatus(order.status),
    pickupWindow: order.pickup_window || "—",
    amount: formatCurrency(order.amount),
  }));
  const salesTrend = (dashboard?.sales_trend || []).map((item) => ({
    ...item,
    label: formatTrendDate(item.date),
  }));
  const status = dashboard?.order_status || {};
  const orderStatus = [
    { label: "Paid", value: status.paid || 0, className: "paid" },
    { label: "Ready for Pickup", value: status.ready_for_pickup || 0, className: "ready" },
    { label: "Collected", value: status.collected || 0, className: "collected" },
    { label: "Cancelled", value: status.cancelled || 0, className: "cancelled" },
  ];
  const donutBackground = buildDonutBackground(orderStatus);
  const topMerchants = (dashboard?.top_merchants || []).map((merchant) => ({
    ...merchant,
    sales: formatCurrency(merchant.sales),
  }));
  const maxSales = Math.max(1, ...salesTrend.map((item) => item.amount));

  return (
    <main className="dashboard-page">
      <div className="dashboard-page__container">
        <ModuleHeader
          bleed
          title="Dashboard"
          description="Monitor today’s marketplace activity, merchant performance, and required actions."
          actions={(
            <>
              <ModuleHeaderAction variant="dashboard" type="button">
                <Icon name="calendar" />
                Today
              </ModuleHeaderAction>
              <ModuleHeaderAction variant="dashboard" type="button" onClick={loadDashboard} disabled={isLoading}>
                <Icon name="refresh" />
                {isLoading ? "Refreshing..." : "Refresh"}
              </ModuleHeaderAction>
              <ModuleHeaderAction variant="dashboard" type="button" onClick={handleExport} disabled={isExporting}>
                {isExporting ? "Exporting..." : "Export Report"}
              </ModuleHeaderAction>
            </>
          )}
        />

        {errorMessage ? (
          <div className="dashboard-page__error" role="alert">
            <span>{errorMessage}</span>
            <button type="button" onClick={loadDashboard}>Try Again</button>
          </div>
        ) : null}

        <section className={`dashboard-page__content-grid ${isLoading && !dashboard ? "dashboard-page__content-grid--loading" : ""}`}>
          <article className="dashboard-page__panel dashboard-page__panel--wide">
            <SectionHeader
              title="Today’s Marketplace Activity"
              description="Live operational activity across all merchants and outlets."
            />
            <div className="dashboard-page__marketplace-grid">
              {marketplaceStats.map((item) => (
                <MetricCard item={item} key={item.label} />
              ))}
            </div>
          </article>

          <article className="dashboard-page__panel dashboard-page__overview-panel">
            <SectionHeader
              title="Merchant Overview"
              description="Approved merchants and merchants currently selling."
            />
            <div className="dashboard-page__compact-grid">
              {merchantStats.map((item) => (
                <MetricCard item={item} key={item.label} />
              ))}
            </div>
          </article>

          <article className="dashboard-page__panel dashboard-page__overview-panel dashboard-page__financial-panel">
            <SectionHeader
              title="Today’s Financial Performance"
              description="Customer payments and SecondBite earnings."
            />
            <div className="dashboard-page__compact-grid">
              {financialStats.map((item) => (
                <MetricCard item={item} key={item.label} />
              ))}
            </div>
          </article>

          <article className="dashboard-page__panel dashboard-page__panel--wide dashboard-page__refund-panel">
            <SectionHeader
              title="Action Required"
              description="Cancelled orders that require admin action."
              action={<span className="dashboard-page__count-badge">{pendingRefunds.length}</span>}
            />
            <div className="dashboard-page__table-wrap">
              <div className="dashboard-page__refund-table">
                <div className="dashboard-page__refund-head">
                  <span>Order No.</span>
                  <span>Merchant</span>
                  <span>Customer</span>
                  <span>Date</span>
                  <span>Amount</span>
                  <span>Status</span>
                  <span>Action</span>
                </div>
                {!isLoading && !pendingRefunds.length ? (
                  <p className="dashboard-page__empty">No Data Available</p>
                ) : (
                  pendingRefunds.map((item) => (
                    <div className="dashboard-page__refund-row" key={item.order}>
                      <NavLink to={`/orders/${item.order}`}>{item.order}</NavLink>
                      <span>{item.merchant}</span>
                      <span>{item.customer}</span>
                      <span>{item.date}</span>
                      <strong>{item.amount}</strong>
                      <span><i className="dashboard-page__badge dashboard-page__badge--cancelled">Cancelled</i></span>
                      <NavLink to={`/orders/${item.order}`}>Review Refund</NavLink>
                    </div>
                  ))
                )}
              </div>
            </div>
          </article>

          <article className="dashboard-page__panel dashboard-page__panel--sales">
            <SectionHeader
              title="Sales Trend"
              description="Daily sales amount for the last 7 days."
            />
            <div className="dashboard-page__chart">
              {salesTrend.map((item) => (
                <button
                  type="button"
                  className="dashboard-page__chart-item"
                  key={item.date}
                  onMouseEnter={() => setHoveredPoint(item.date)}
                  onMouseLeave={() => setHoveredPoint(null)}
                  onFocus={() => setHoveredPoint(item.date)}
                  onBlur={() => setHoveredPoint(null)}
                >
                  <span>{formatCurrency(item.amount)}</span>
                  <i style={{ height: `${Math.max(12, (item.amount / maxSales) * 100)}%` }} />
                  <small>{item.label}</small>
                  {hoveredPoint === item.date ? (
                    <span className="dashboard-page__tooltip">
                      <strong>{item.label}</strong>
                      <span>{formatCurrency(item.amount)}</span>
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          </article>

          <article className="dashboard-page__panel">
            <SectionHeader
              title="Order Status"
              description="Today’s order distribution."
            />
            <div className="dashboard-page__status-layout">
              <div className="dashboard-page__donut" style={{ background: donutBackground }}>
                <span>{marketplace.orders || 0}</span>
                <small>Total Orders</small>
              </div>
              <div className="dashboard-page__legend">
                {orderStatus.map((item) => (
                  <div key={item.label}>
                    <i className={`dashboard-page__legend-dot dashboard-page__legend-dot--${item.className}`} />
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
            </div>
          </article>

          <article className="dashboard-page__panel dashboard-page__panel--wide">
            <SectionHeader
              title="Recent Orders"
              description="Latest orders across all merchants and outlets."
              action={<NavLink to="/orders" className="dashboard-page__text-link">View All</NavLink>}
            />
            <div className="dashboard-page__table-wrap">
              <div className="dashboard-page__orders-table">
                <div className="dashboard-page__orders-head">
                  <span>Order No.</span>
                  <span>Merchant / Outlet</span>
                  <span>Customer</span>
                  <span>Status</span>
                  <span>Pickup Window</span>
                  <span>Amount</span>
                </div>
                {!isLoading && !recentOrders.length ? (
                  <p className="dashboard-page__empty">No Data Available</p>
                ) : (
                  recentOrders.map((order) => (
                    <div className="dashboard-page__orders-row" key={order.order}>
                      <NavLink to={`/orders/${order.order}`}>{order.order}</NavLink>
                      <span className="dashboard-page__merchant-outlet">
                        <strong>{order.merchant}</strong>
                        <small>{order.outlet}</small>
                      </span>
                      <span>{order.customer}</span>
                      <span>
                        <i className={`dashboard-page__badge dashboard-page__badge--${order.status.toLowerCase().replace(/\s+/g, "-")}`}>
                          {order.status}
                        </i>
                      </span>
                      <span>{order.pickupWindow}</span>
                      <strong>{order.amount}</strong>
                    </div>
                  ))
                )}
              </div>
            </div>
          </article>

          <article className="dashboard-page__panel dashboard-page__panel--wide">
            <SectionHeader
              title="Top Performing Merchants"
              description="Highest sales performance today."
            />
            <div className="dashboard-page__merchant-list">
              {!isLoading && !topMerchants.length ? (
                <p className="dashboard-page__empty">No Data Available</p>
              ) : (
                topMerchants.map((merchant, index) => (
                  <div key={merchant.name}>
                    <span className="dashboard-page__rank">{index + 1}</span>
                    <span>
                      <strong>{merchant.name}</strong>
                      <small>{merchant.orders} orders</small>
                    </span>
                    <strong>{merchant.sales}</strong>
                  </div>
                ))
              )}
            </div>
          </article>
        </section>
      </div>
    </main>
  );
};

export default DashboardPage;
